import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { CommandPalette } from './CommandPalette';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  Package,
  CreditCard,
  GitFork,
  Archive,
  FlaskConical,
  Globe,
  FileText,
  ShieldAlert,
  Bell,
  LifeBuoy,
  Mail,
  LogOut,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  Sun,
  Moon,
  Search,
  Command,
  Layers,
  UserCog,
  Server,
  Radio,
} from 'lucide-react';

interface NavSection {
  title: string;
  items: {
    to: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    badge?: number;
  }[];
}

export const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    localStorage.setItem('admin_theme', nextTheme);
    if (nextTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { data: pendingRegistrationsCount = 0 } = useQuery({
    queryKey: ['sidebar-pending-registrations'],
    queryFn: async () => {
      try {
        const response = await api.get('/admin/clients/pending');
        const list = response.data?.data || response.data || [];
        return Array.isArray(list) ? list.length : 0;
      } catch {
        return 0;
      }
    },
    refetchInterval: 30000,
  });

  const { data: pendingSubscriptionsCount = 0 } = useQuery({
    queryKey: ['sidebar-pending-subscriptions'],
    queryFn: async () => {
      try {
        const response = await api.get('/admin/subscriptions', {
          params: { status: 'pending_payment', limit: 100 },
        });
        const list = response.data?.data || response.data || [];
        return Array.isArray(list) ? list.length : 0;
      } catch {
        return 0;
      }
    },
    refetchInterval: 30000,
  });

  const { data: openTicketsCount = 0 } = useQuery({
    queryKey: ['sidebar-open-tickets'],
    queryFn: async () => {
      try {
        const response = await api.get('/admin/support-tickets', {
          params: { status: 'open', limit: 100 },
        });
        const list = response.data?.data || response.data || [];
        return Array.isArray(list) ? list.length : 0;
      } catch {
        return 0;
      }
    },
    refetchInterval: 30000,
  });

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    Clientes: true,
    'Catálogo & Pagamentos': true,
    'Gateway & Integração': true,
    'Monitorização & Logs': true,
    'Suporte & E-mails': false,
    'Equipa & Segurança': true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const sections: NavSection[] = [
    {
      title: 'Clientes',
      items: [
        {
          to: '/registrations',
          label: 'Registos Pendentes',
          icon: UserCheck,
          badge: pendingRegistrationsCount,
        },
        { to: '/clients', label: 'Lista de Clientes', icon: Users },
        {
          to: '/subscriptions',
          label: 'Subscrições & Quotas',
          icon: Layers,
          badge: pendingSubscriptionsCount,
        },
      ],
    },
    {
      title: 'Catálogo & Pagamentos',
      items: [
        { to: '/products', label: 'Catálogo de Produtos', icon: Package },
        { to: '/payment-methods', label: 'Métodos de Pagamento', icon: CreditCard },
      ],
    },
    {
      title: 'Gateway & Integração',
      items: [
        { to: '/kong-sync', label: 'Sincronização Kong', icon: Server },
        { to: '/integration-routes', label: 'Rotas de Integração', icon: GitFork },
        { to: '/configuration', label: 'Backup & Configurações', icon: Archive },
        { to: '/sandbox-mocks', label: 'Sandbox Mocks', icon: FlaskConical },
        { to: '/cors-approvals', label: 'Aprovações CORS', icon: Globe },
      ],
    },
    {
      title: 'Monitorização & Logs',
      items: [
        { to: '/api-logs', label: 'Logs de API', icon: FileText },
        { to: '/webhooks-dlq', label: 'Webhooks & DLQ', icon: Radio },
        { to: '/audit-logs', label: 'Logs de Auditoria', icon: ShieldAlert },
        { to: '/alerts', label: 'Alertas do Sistema', icon: Bell },
      ],
    },
    {
      title: 'Suporte & E-mails',
      items: [
        {
          to: '/support',
          label: 'Tickets de Suporte',
          icon: LifeBuoy,
          badge: openTicketsCount,
        },
        { to: '/notifications', label: 'Templates de E-mail', icon: Mail },
      ],
    },
    {
      title: 'Equipa & Segurança',
      items: [
        { to: '/admin-users', label: 'Equipa & Administradores', icon: UserCog },
        { to: '/mfa-setup', label: 'Segurança MFA (2FA)', icon: ShieldCheck },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 font-sans antialiased text-slate-900 dark:text-slate-100 transition-colors">
      {/* Modern Fixed Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 z-30 flex flex-col justify-between shadow-sm">
        <div className="flex-1 flex flex-col min-h-0">
          {/* Header Brand */}
          <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-primary-600 to-indigo-500 flex items-center justify-center text-white shadow-md shadow-primary-500/20 group-hover:scale-105 transition-transform">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h1 className="font-bold text-base leading-tight text-slate-900 dark:text-white">
                  Admin Portal
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  TS Dev Gateway
                </p>
              </div>
            </Link>
          </div>

          {/* Navigation Links */}
          <nav className="p-3.5 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            {/* Dashboard Direct Link */}
            <Link
              to="/"
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive('/')
                  ? 'bg-primary-50 text-primary-600 dark:bg-primary-950/40 dark:text-primary-400 font-semibold shadow-xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800/50'
              }`}
            >
              <LayoutDashboard
                className={`w-4 h-4 ${isActive('/') ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400'}`}
              />
              Painel de Controlo
            </Link>

            {/* Collapsible Sections */}
            {sections.map((section) => {
              const isExpanded = expandedSections[section.title] ?? true;
              const hasActiveItem = section.items.some((item) => isActive(item.to));

              return (
                <div key={section.title} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.title)}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
                  >
                    <span className="flex items-center gap-1.5 truncate whitespace-nowrap">
                      {section.title}
                      {hasActiveItem && (
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-600 dark:bg-primary-400 shrink-0" />
                      )}
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="space-y-0.5 pt-0.5">
                      {section.items.map((item) => {
                        const active = isActive(item.to);
                        const Icon = item.icon;

                        return (
                          <Link
                            key={item.to}
                            to={item.to}
                            className={`flex items-center justify-between px-3.5 py-2 rounded-xl text-sm transition-all ${
                              active
                                ? 'bg-primary-50 text-primary-600 dark:bg-primary-950/40 dark:text-primary-400 font-semibold shadow-xs'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800/50 font-medium'
                            }`}
                          >
                            <div className="flex items-center gap-3 truncate min-w-0">
                              <Icon
                                className={`w-4 h-4 shrink-0 ${active ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400'}`}
                              />
                              <span className="truncate">{item.label}</span>
                            </div>
                            {Boolean(item.badge && item.badge > 0) && (
                              <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800 shrink-0">
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Live System Status & User Profile */}
        <div className="shrink-0 p-3.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 space-y-3">
          {/* Live Status indicator */}
          <div className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/50 dark:border-emerald-800/30 text-xs text-emerald-800 dark:text-emerald-300 font-medium">
            <span className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Kong Gateway
            </span>
            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold uppercase">
              Online
            </span>
          </div>

          {user && (
            <div className="flex items-center justify-between p-1.5 rounded-xl bg-white dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 shadow-xs">
              <div className="flex items-center gap-2.5 min-w-0 pl-1">
                <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 flex items-center justify-center font-bold text-xs uppercase shrink-0">
                  {user.email.slice(0, 2)}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-900 dark:text-white truncate">
                    {user.email}
                  </p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 capitalize">
                    {user.role}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors"
                title="Terminar Sessão"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area with Modern Sticky Header */}
      <div className="flex-1 min-w-0 ml-64 min-h-screen flex flex-col">
        {/* Top Navbar */}
        <header className="h-16 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md px-8 flex items-center justify-between sticky top-0 z-20">
          {/* Quick Search shortcut */}
          <button
            type="button"
            onClick={() => setIsCommandPaletteOpen(true)}
            className="flex items-center gap-2.5 text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/60 px-3.5 py-1.5 rounded-xl border border-slate-200/60 dark:border-slate-700/60 hover:border-primary-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors text-left"
          >
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <span>Pesquisa rápida em clientes, produtos ou logs...</span>
            <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 font-mono text-[10px] text-slate-600 dark:text-slate-300 font-semibold shadow-2xs">
              <Command className="w-2.5 h-2.5" /> K
            </kbd>
          </button>

          {/* Right Header Actions */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle Button */}
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-xl text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent dark:border-slate-800 transition-colors"
              title={theme === 'dark' ? 'Mudar para Modo Claro' : 'Mudar para Modo Escuro'}
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4 text-amber-400" />
              ) : (
                <Moon className="w-4 h-4 text-slate-600" />
              )}
            </button>

            {/* Admin Badge */}
            {user && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="capitalize">{user.role}</span>
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-8 animate-fade-in">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Global Command Palette (Cmd + K) */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />
    </div>
  );
};
